import { DocumentOptions } from '../../application/domain/entities/DocumentOptions';
import { HttpClient } from '@/shared';
import Section2, {
  Section1Summary,
  DynamicFieldSchema,
  DocumentList,
} from '../../application/domain/entities/Section2';
import { ISection2Repository } from '../../application/ports/outbound/ISection2Repository';
import { Section2Form } from '../../application/validators/section-2.schema';
import { Section2Steps } from '../../application/domain/models/section-2-steps';
import { Section2Response, Section2ResponseDto } from './dto/res/Section2ResponseDto';
import {
  mapSection2MutationResponseDtoToEntity,
  mapSection2FormToDto,
  mapSection2QueryResposeDtoToEntity,
  mapSectionOneDtoToSummary,
} from './mappers/section2Mapper';
import { SectionOneResponseDto } from '@/features/section-1/infra/repositories/dto/res/SectionOneResponseDto';
import { mapDocumentTypesDtoToEntity } from './mappers/documentTypesMapper';
import { DocumentTypesResponseDto } from './dto/res/DocumentTypesResponseDto';
import { DocumentSchemaDataDto } from './dto/res/DocumentSchemaResponseDto';
import { mapDocumentSchemaDtoToEntity } from './mappers/documentSchemaMapper';
import { BlobDocumentResponseDto } from './dto/res/BlobDocumentResponseDto';
import { CustomDocumentTypeDto } from './dto/res/CustomDocumentTypeResponseDto';
import { mapCustomDocumentTypeDtoToEntity } from './mappers/customDocumentTypeMapper';
import CustomDocumentType from '../../application/domain/entities/CustomDocumentType';
import { I9FormLocationResponseDto } from './dto/res/I9FormLocationResponseDto';
import { mapI9FormLocationDtoToEntity } from './mappers/i9FormLocationMapper';
import { I9FormLocation } from '../../application/domain/entities/I9FormLocation';
import type {
  DocumentFileUpload,
  DocumentUploads,
} from '@/features/section-2/application/domain/entities/DocumentUploads';

export interface FileUploadResponse {
  url: string;
  hash: string;
}

export class ApiSection2Repository implements ISection2Repository {
  constructor(readonly httpClient: HttpClient) {}

  /**
   * Fetch Section 1 summary data
   */
  async fetchSection1Summary(i9FormId: string): Promise<Section1Summary> {
    const sectionOne = await this.httpClient.get<SectionOneResponseDto>(
      `/i9Form/${i9FormId}/section1`,
      {
        params: {
          isEncrypted: false,
        },
      }
    );

    return mapSectionOneDtoToSummary(sectionOne.data);
  }

  /**
   * Fetch Section 2 data
   */
  async getSection2(i9FormId: string): Promise<Section2> {
    const response = await this.httpClient.get<Section2Response>(`/section2/${i9FormId}`);
    return mapSection2QueryResposeDtoToEntity(response.data);
  }

  /**
   * Upload a file to blob storage
   * Returns the URL of the uploaded file
   */
  async uploadFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await this.httpClient.post<FileUploadResponse>('/blob/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Upsert Section 2 data
   * Document metadata now only contains URLs (files are uploaded separately)
   */
  async upsertSection2(
    formValues: Section2Form,
    currentStep: Section2Steps,
    i9FormId: string
  ): Promise<Section2> {
    // Convert form values to DTO using mapper
    const requestDto = mapSection2FormToDto(formValues, currentStep, i9FormId);

    // Simple JSON POST - no file handling needed here
    const response = await this.httpClient.patch<Section2ResponseDto>(`/section2`, requestDto);
    return mapSection2MutationResponseDtoToEntity(response.data);
  }

  /**
   * Fetch document schema for a specific list type and selection
   */
  async fetchDocumentSchema(
    documentSelection: string,
    i9FormId?: string,
    opts?: {
      citizenshipStatus?: string;
      dateOfBirth?: string;
      list?: 'A' | 'B' | 'C';
      documentTypeCode?: string;
      /** Supplement B: reverification is held to the matrix for every I-9 (VTK-2062). */
      forReverification?: boolean;
    }
  ): Promise<DynamicFieldSchema[]> {
    const response = await this.httpClient.get<DocumentSchemaDataDto>(
      '/section2/documents/schema',
      {
        // axios omits undefined params. i9FormId (Section 2) OR citizenshipStatus (+dateOfBirth)
        // for the in-progress Section 1 employee-document flow.
        params: {
          docType: documentSelection,
          i9FormId,
          citizenshipStatus: opts?.citizenshipStatus,
          dateOfBirth: opts?.dateOfBirth,
          list: opts?.list,
          documentTypeCode: opts?.documentTypeCode,
          forReverification: opts?.forReverification || undefined,
        },
      }
    );
    return mapDocumentSchemaDtoToEntity(response.data.fields);
  }

  async fetchDocumentFiles(i9FormId?: string): Promise<DocumentUploads> {
    const response = await this.httpClient.get<DocumentUploads>(
      `/section2/${i9FormId}/document-files`
    );
    return response.data;
  }

  async recordDocumentFile(
    i9FormId: string | undefined,
    upload: DocumentFileUpload
  ): Promise<DocumentUploads> {
    const response = await this.httpClient.post<DocumentUploads>(
      `/section2/${i9FormId}/document-files`,
      upload
    );
    return response.data;
  }

  async fetchDocumentOptions(
    i9FormId?: string,
    statusCode?: string,
    opts?: { forReverification?: boolean }
  ): Promise<DocumentOptions> {
    const response = await this.httpClient.get<DocumentOptions>('/section2/documents/options', {
      params: { i9FormId, statusCode, forReverification: opts?.forReverification || undefined },
    });
    return response.data;
  }

  /**
   * Fetch all available document types from the API
   */
  async fetchDocumentTypes(
    i9FormId?: string,
    opts?: { citizenshipStatus?: string }
  ): Promise<DocumentList[]> {
    const response = await this.httpClient.get<DocumentTypesResponseDto>(
      '/section2/documents/types',
      {
        params: {
          i9FormId,
          citizenshipStatus: opts?.citizenshipStatus,
        },
      }
    );
    return mapDocumentTypesDtoToEntity(response.data);
  }

  /**
   * Download a document from blob storage
   * Returns base64 encoded document data
   */
  async downloadDocument(documentUrl: string): Promise<string> {
    const response = await this.httpClient.get<BlobDocumentResponseDto>('/blob/document', {
      params: {
        url: documentUrl,
      },
    });

    return response.data.document;
  }

  /**
   * Fetch distinct document types
   */
  async fetchDistinctDocumentTypes(): Promise<CustomDocumentType[]> {
    const response = await this.httpClient.get<CustomDocumentTypeDto[]>(
      '/section2/documents/distinct-types'
    );
    return response.data.map(mapCustomDocumentTypeDtoToEntity);
  }

  /**
   * Approve a Remote I-9 form
   */
  async approveRemoteI9(i9FormId: string): Promise<{ everifyCaseQueueId: string }> {
    const response = await this.httpClient.post<{ everifyCaseQueueId: string }>(
      `/remote-i9/approve/${i9FormId}`
    );
    return response.data;
  }

  /**
   * Approve an in-person Section 2 back in review after a Section 1 correction (VTK-2182)
   */
  async approveInPersonReview(i9FormId: string): Promise<{ everifyCaseQueueId: string }> {
    const response = await this.httpClient.post<{ everifyCaseQueueId: string }>(
      `/section2/${i9FormId}/approve`
    );
    return response.data;
  }

  /**
   * Fetch location details associated with an I-9 form
   */
  async fetchI9FormLocation(i9FormId: string): Promise<I9FormLocation> {
    const response = await this.httpClient.get<I9FormLocationResponseDto>(
      `/i9Form/${i9FormId}/locations`
    );
    return mapI9FormLocationDtoToEntity(response.data);
  }

  async fetchAlternativeProcedureAllowed(i9FormId: string): Promise<boolean> {
    const response = await this.httpClient.get<{ alternativeProcedureAllowed: boolean }>(
      `/section2/${i9FormId}/alternative-procedure`
    );
    return response.data.alternativeProcedureAllowed === true;
  }
}
